import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import client from '../../api/client';

/**
 * Renders an authenticated receipt preview (image or PDF).
 * Uses apiClient with JWT to fetch the file as a blob,
 * then creates a local blob: URL for the <img> or <iframe> tag.
 * Cleans up the blob URL on unmount.
 */
export default function AuthedReceiptPreview({ src, fileType, alt, style, imgStyle, iframeStyle }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!src) { setLoading(false); return; }
    let cancelled = false;
    let objectUrl = null;
    setLoading(true);
    setError(false);
    setBlobUrl(null);
    client.get(src, { responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    // Revoke the URL created by THIS effect run (not the state value, which
    // would be stale here) — only on unmount or when src changes, never while
    // it is still bound to the live <img>/<iframe>.
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ...style }}>
        <Loader2 size={16} className="spin" style={{ color: '#94a3b8' }} />
      </div>
    );
  }

  if (error || !blobUrl) return null;

  const isImage = fileType?.startsWith('image/');
  const isPdf = fileType === 'application/pdf';

  if (isImage) {
    return <img src={blobUrl} alt={alt || 'recibo'} style={imgStyle || style} />;
  }

  if (isPdf) {
    return <iframe src={blobUrl} title={alt || 'recibo'} style={iframeStyle || style} />;
  }

  return null;
}
